import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  input,
  signal,
  viewChild
} from '@angular/core';
import { computePosition, flip, offset, shift } from '@floating-ui/dom';
import { TranslatePipe } from '@ngx-translate/core';

import { ICONS } from '@core/services/icons/icons.service';

import {
  OccupationClaim,
  OccupationForm,
  OccupationSeries,
  OccupationStep
} from '../models/occupation.model';
import {
  effectiveForm,
  formatHourLabel,
  hourTicks,
  makeTimeScale,
  seriesBounds
} from '../occupation-scales';
import { OccupationBlockTooltipComponent } from '../occupation-tooltip/occupation-block-tooltip.component';
import { OccupationTooltipComponent } from '../occupation-tooltip/occupation-tooltip.component';

/**
 * Width every non-compact panel is drawn at, and the CSS width it is capped to, so the
 * page renders it one-to-one instead of scaling its 8px labels up with the container.
 */
const PANEL_WIDTH = 830;

/** Geometry of one rendered panel, in SVG user units. */
interface PanelGeometry {
  width: number;
  height: number;
  padLeft: number;
  padTop: number;
  padBottom: number;
}

interface AreaMark {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface LadderBlockView {
  x: number;
  width: number;
  routeUid: string;
  stopoverUid: string;
  overflow: boolean;
  label: string | null;
  /** The claim behind the block, so a hover can report its window and quantity. */
  claim: OccupationClaim;
  laneIndex: number;
}

export interface LadderLaneView {
  index: number;
  overflow: boolean;
  y: number;
  height: number;
  blocks: LadderBlockView[];
}

export interface LadderPanel {
  series: OccupationSeries;
  label: string;
  geometry: PanelGeometry;
  lanes: LadderLaneView[];
  ticks: Array<{ x: number; label: string }>;
  capacityLineY: number | null;
}

/** Identifies a panel among the several the chart may render at once. */
function panelKey(series: OccupationSeries): string {
  return `${series.siteUid}/${series.infrastructureUid}`;
}

/** One panel's fully resolved drawing instructions. */
export interface AreaPanel {
  series: OccupationSeries;
  label: string;
  geometry: PanelGeometry;
  /** The usage step outline, as an SVG path `d`. */
  usagePath: string;
  /** The filled wash beneath the outline. */
  areaPath: string;
  /** Rectangles covering only the part of usage above capacity. */
  breaches: AreaMark[];
  capacityY: number | null;
  baselineY: number;
  ticks: Array<{ x: number; label: string }>;
  yLabels: Array<{ y: number; label: string }>;
  /** True when this panel stands in for a ladder whose capacity cannot be drawn as lanes. */
  degraded: boolean;
  /** Transparent full-height columns: the drawn 2px line is not a usable hover target. */
  hits: Array<{
    x: number;
    width: number;
    height: number;
    step: OccupationStep;
  }>;
}

@Component({
  selector: 'app-occupation-chart',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [OccupationTooltipComponent, OccupationBlockTooltipComponent, TranslatePipe],
  templateUrl: './occupation-chart.component.html',
  styleUrl: './occupation-chart.component.css'
})
export class OccupationChartComponent {
  protected readonly ICONS = ICONS;
  readonly series = input.required<OccupationSeries[]>();
  readonly form = input<OccupationForm>('area');
  /** Compact geometry for the 300px popup. */
  readonly compact = input<boolean>(false);
  /**
   * Optional [startMs, endMs] of the stopover the chart was opened from, drawn as an
   * outline over the step-area form so the reader can locate that step within the day.
   */
  readonly markWindowMs = input<[number, number] | null>(null);

  /**
   * The span every form shares. The mark window is folded in: a step whose interval falls
   * outside the plotted usage — a wait at a site whose docks are busy elsewhere in the day,
   * or a site with declared capacity and no usage at all — would otherwise have its outline
   * scaled off the canvas and clipped away by the SVG viewport.
   */
  private readonly bounds = computed(() => {
    const window = this.markWindowMs();
    if (!window) return seriesBounds(this.series());

    const plotted = this.series().some((s) => s.steps.length > 0);
    if (!plotted) return seriesBounds([], window);

    return seriesBounds(this.series(), window);
  });

  /**
   * Whether the plan reported any usage at all. Every series carries the same set-level
   * verdict, and an empty chart legitimately reports nothing, so `every` answers for all.
   */
  protected readonly usagesReported = computed(() =>
    this.series().every((s) => s.usagesReported)
  );

  /**
   * The route under the cursor. There is no per-route hue by design, so emphasis is the
   * only channel carrying route identity in the ladder.
   */
  protected readonly hoveredRoute = signal<string | null>(null);

  /**
   * The step under the cursor, tagged with the panel it belongs to: every panel renders
   * the same tooltip template, so an untagged hover would open one tooltip per panel.
   */
  protected readonly hovered = signal<{
    panelKey: string;
    step: OccupationStep;
    capacity: number | null;
    /** The mark itself, which Floating UI positions the tooltip against. */
    reference: Element;
  } | null>(null);

  /**
   * The ladder block under the cursor, tagged with its panel for the same reason `hovered`
   * is: every panel renders the same tooltip template.
   */
  protected readonly hoveredBlock = signal<{
    panelKey: string;
    block: LadderBlockView;
    reference: Element;
  } | null>(null);

  protected hoveredIn(panel: AreaPanel): {
    step: OccupationStep;
    capacity: number | null;
    reference: Element;
  } | null {
    const hovered = this.hovered();
    return hovered && hovered.panelKey === panelKey(panel.series) ? hovered : null;
  }

  protected hoveredBlockIn(panel: LadderPanel): LadderBlockView | null {
    const hovered = this.hoveredBlock();
    return hovered && hovered.panelKey === panelKey(panel.series) ? hovered.block : null;
  }

  // `read: ElementRef` is required: a template reference on a component tag resolves to the
  // component instance, which has no element to position.
  private readonly areaTooltip = viewChild('areaTooltip', { read: ElementRef });
  private readonly blockTooltip = viewChild('blockTooltip', { read: ElementRef });

  constructor() {
    // The cursor cannot leave a mark that has been replaced under it, so its mouseleave
    // never fires: without this the stale tooltip reappears when the form cycles back.
    effect(() => {
      this.form();
      this.series();
      this.hovered.set(null);
      this.hoveredBlock.set(null);
      this.hoveredRoute.set(null);
    });

    effect(() => {
      const hovered = this.hovered();
      const floating = this.areaTooltip()?.nativeElement;
      if (hovered && floating) this.place(hovered.reference, floating);
    });

    effect(() => {
      const hovered = this.hoveredBlock();
      const floating = this.blockTooltip()?.nativeElement;
      if (hovered && floating) this.place(hovered.reference, floating);
    });
  }

  /**
   * Positions a tooltip against the mark it describes, the same way every other tooltip in
   * the app is positioned.
   *
   * Above the mark by default, so the tooltip never covers what it is describing; `flip`
   * moves it below only where there is genuinely no room, and `shift` keeps it on screen
   * near the edges. `fixed` takes it out of the panel's scrolling ancestor, which would
   * otherwise clip it on a short chart.
   */
  private place(reference: Element, floating: HTMLElement): void {
    computePosition(reference, floating, {
      strategy: 'fixed',
      placement: 'top',
      middleware: [offset(8), flip(), shift({ padding: 8 })]
    }).then(({ x, y }) => {
      floating.style.left = `${x}px`;
      floating.style.top = `${y}px`;
    });
  }

  protected panelKey(series: OccupationSeries): string {
    return panelKey(series);
  }

  /** The mark window in SVG user units for a panel of the given geometry, or null. */
  protected markRect(
    g: PanelGeometry
  ): { x: number; width: number; y: number; height: number } | null {
    const window = this.markWindowMs();
    if (!window) return null;
    const { t0, t1 } = this.bounds();
    const x = makeTimeScale(t0, t1, g.padLeft, g.width - g.padLeft - 6);
    return {
      x: x(window[0]),
      width: Math.max(2, x(window[1]) - x(window[0])),
      y: g.padTop - 1,
      height: g.height - g.padTop - g.padBottom + 2
    };
  }

  protected readonly areaPanels = computed<AreaPanel[]>(() =>
    this.series()
      .filter((s) => effectiveForm(this.form(), s) === 'area')
      .map((s) => this.buildAreaPanel(s))
  );

  /**
   * Series the ladder cannot represent at all, listed rather than charted. Drawing a step
   * area under a control that says "slot ladder" reads as a control that only half worked.
   */
  protected readonly unavailablePanels = computed<OccupationSeries[]>(() =>
    this.series().filter((s) => effectiveForm(this.form(), s) === 'unavailable')
  );

  protected readonly ladderPanels = computed<LadderPanel[]>(() =>
    this.series()
      .filter((s) => effectiveForm(this.form(), s) === 'ladder')
      .map((s) => this.buildLadderPanel(s))
  );

  private buildLadderPanel(s: OccupationSeries): LadderPanel {
    const compact = this.compact();
    const laneHeight = compact ? 12 : 17;
    const gap = 2; // the surface gap — never a stroke around a mark
    const padLeft = compact ? 20 : 26;
    const padTop = 4;
    const padBottom = compact ? 13 : 16;
    const width = compact ? 276 : PANEL_WIDTH;

    const capacity = s.capacity ?? 0;
    const laneCount = Math.max(capacity, s.lanes.length);
    const height = padTop + laneCount * (laneHeight + gap) - gap + padBottom;
    const innerWidth = width - padLeft - 6;

    const { t0, t1 } = this.bounds();
    const x = makeTimeScale(t0, t1, padLeft, innerWidth);
    const laneY = (i: number) => padTop + i * (laneHeight + gap);

    const lanes: LadderLaneView[] = [];
    for (let i = 0; i < laneCount; i++) {
      const overflow = i >= capacity;
      const claims = s.lanes[i] ?? [];
      lanes.push({
        index: i,
        overflow,
        y: laneY(i),
        height: laneHeight,
        blocks: claims.map((c) => {
          const bx = x(c.startMs);
          const bw = Math.max(2, x(c.endMs) - bx);
          // Only label when the text measurably fits: a clipped label is worse than none.
          const needed = c.routeUid.length * (compact ? 4.6 : 5.2) + 12;
          return {
            x: bx,
            width: bw,
            routeUid: c.routeUid,
            stopoverUid: c.stopoverUid,
            overflow,
            label: bw > needed && laneHeight >= 13 ? c.routeUid : null,
            claim: c,
            laneIndex: i
          };
        })
      });
    }

    return {
      series: s,
      label: `${s.infrastructureName}: ${s.lanes.length} slots used of ${capacity} available`,
      geometry: { width, height, padLeft, padTop, padBottom },
      lanes,
      ticks: hourTicks(t0, t1, compact ? 4 : 7).map((t) => ({
        x: x(t),
        label: formatHourLabel(t)
      })),
      capacityLineY: s.lanes.length > capacity ? laneY(capacity) - gap / 2 : null
    };
  }

  /**
   * The SVG scales to fill its container, so these numbers set the size text and strokes
   * are read at. The non-compact width matches the ladder's: cycling forms must not resize
   * the chart under the reader, and a narrower viewBox stretched to the same panel would
   * blow an 8px tick label up to a heading.
   */
  private geometry(): PanelGeometry {
    return this.compact()
      ? { width: 276, height: 74, padLeft: 20, padTop: 8, padBottom: 13 }
      : { width: PANEL_WIDTH, height: 180, padLeft: 26, padTop: 8, padBottom: 17 };
  }

  private buildAreaPanel(s: OccupationSeries): AreaPanel {
    const g = this.geometry();
    const { t0, t1 } = this.bounds();
    const innerWidth = g.width - g.padLeft - 6;
    const innerHeight = g.height - g.padTop - g.padBottom;

    const x = makeTimeScale(t0, t1, g.padLeft, innerWidth);
    const yMax = Math.max(s.capacity ?? 0, s.peak) * 1.18 || 1;
    const y = (v: number) => g.padTop + innerHeight - (v / yMax) * innerHeight;

    const outline: string[] = [];
    const wash: string[] = [];
    const breaches: AreaMark[] = [];

    s.steps.forEach((step: OccupationStep, i: number) => {
      const x0 = x(step.fromMs);
      const x1 = x(step.toMs);
      const capped = s.capacity === null ? step.used : Math.min(step.used, s.capacity);

      outline.push(
        `${i === 0 ? 'M' : 'L'} ${x0} ${y(step.used)}`,
        `L ${x1} ${y(step.used)}`
      );
      // Always `L`: `areaPath` opens the only subpath, at the baseline. A second moveto
      // here would discard that start, and `Z` would close the fill diagonally back to
      // the first step instead of down to the baseline.
      wash.push(`L ${x0} ${y(capped)}`, `L ${x1} ${y(capped)}`);

      if (s.capacity !== null && step.used > s.capacity) {
        breaches.push({
          x: x0,
          y: y(step.used),
          width: Math.max(1, x1 - x0),
          height: y(s.capacity) - y(step.used)
        });
      }
    });

    const baselineY = y(0);
    const areaPath = wash.length
      ? `M ${x(s.steps[0].fromMs)} ${baselineY} ${wash.join(' ')} L ${x(
          s.steps[s.steps.length - 1].toMs
        )} ${baselineY} Z`
      : '';

    const yLabels = [{ y: baselineY, label: '0' }];
    if (s.capacity !== null) {
      yLabels.push({ y: y(s.capacity), label: String(s.capacity) });
    }

    return {
      series: s,
      label: `${s.infrastructureName}: peak ${s.peak}${
        s.capacity === null ? ', no declared capacity' : ` of ${s.capacity} available`
      }`,
      geometry: g,
      usagePath: outline.join(' '),
      areaPath,
      breaches,
      capacityY: s.capacity === null ? null : y(s.capacity),
      baselineY,
      ticks: hourTicks(t0, t1, this.compact() ? 4 : 7).map((t) => ({
        x: x(t),
        label: formatHourLabel(t)
      })),
      yLabels,
      degraded: this.form() === 'ladder',
      hits: s.steps.map((step) => ({
        x: x(step.fromMs),
        width: Math.max(3, x(step.toMs) - x(step.fromMs)),
        height: innerHeight,
        step
      }))
    };
  }
}
